"use client";

import { createContext, useContext, useEffect, useMemo, useState, ReactNode } from "react";

interface CustomerIdContextValue {
  customerId: string | null;
  userId: string | null;
  setCustomerId: (customerId: string) => void;
}

const CustomerIdContext = createContext<CustomerIdContextValue>({
  customerId: null,
  userId: null,
  setCustomerId: () => {},
});

interface CustomerIdProviderProps {
  customerId: string | null;
  userId: string | null;
  children: ReactNode;
}

export function CustomerIdProvider({
  customerId: initialCustomerId,
  userId,
  children,
}: CustomerIdProviderProps) {
  // The initial value comes from the SSR cookie. On first login that cookie isn't
  // set yet, so it's null and must be updated client-side once useMyUser resolves
  // the customer. Keeping this in state (instead of a static prop) lets
  // customer-scoped queries (dashboards, etc.) enable themselves without a reload.
  const [customerId, setCustomerId] = useState<string | null>(initialCustomerId);

  // Sync if a later SSR render provides a (real) cookie value.
  useEffect(() => {
    if (initialCustomerId) setCustomerId(initialCustomerId);
  }, [initialCustomerId]);

  // userId comes straight from the JWT (pylo_user_id) and is available on the very
  // first login, so it can be a static value. Permission queries read it to fire
  // immediately (filtering AROs by user id) instead of waiting for the `me` query.
  const value = useMemo(() => ({ customerId, userId, setCustomerId }), [customerId, userId]);

  return <CustomerIdContext.Provider value={value}>{children}</CustomerIdContext.Provider>;
}

export function useCustomerId(): string | null {
  return useContext(CustomerIdContext).customerId;
}

export function useCurrentUserId(): string | null {
  return useContext(CustomerIdContext).userId;
}

export function useSetCustomerId(): (customerId: string) => void {
  return useContext(CustomerIdContext).setCustomerId;
}
